import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import type { WizardStepContext } from "@/base";
import { ApiError } from "@/lib/api";
import { messageFor } from "@/lib/errors";
import { getSubmissionPersonnel } from "@/lib/submissions/brokerSubmissions";
import {
  withoutPersonnel,
  withPersonnel,
  type WcDraft,
} from "@/lib/submissions/wc/draft";

/**
 * Reads the submission's people (PMS-S740) into the draft when a step that
 * shows them opens: the People step and the Business step's contacts (BRK-S303,
 * BRK-S337). Only they need them, so `load` doesn't fetch them. The read goes
 * through `setLoaded`, so it is never an unsaved change (BRK-S307).
 *
 * A 404 isn't a failure: the submission was just read, so it means this PMS
 * doesn't keep a submission's people yet, and the draft says so. Anything else
 * is a problem the step shows, with Retry.
 */
export function useSubmissionPeople(
  draft: WcDraft,
  setLoaded: WizardStepContext<WcDraft>["setLoaded"],
): { problem: string | null; retry: () => void } {
  const { authedRequest } = useAuth();
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const submissionId = draft.submission?.id;
  const needed = draft.personnel === null && !draft.personnelUnavailable;

  useEffect(() => {
    if (!needed || !submissionId) return;
    let cancelled = false;
    setProblem(null);
    getSubmissionPersonnel(authedRequest, submissionId).then(
      (personnel) => {
        if (!cancelled) setLoaded((d) => withPersonnel(d, personnel));
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoaded((d) => withoutPersonnel(d));
        } else {
          setProblem(messageFor(err));
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needed, submissionId, attempt, authedRequest, setLoaded]);

  return { problem, retry: () => setAttempt((n) => n + 1) };
}
